import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { reportsApi } from '../../../api/client';
import RangeBar from './components/RangeBar';
import ExportButtons from './components/ExportButtons';
import ReportTable from './components/ReportTable';
import useRangeState from './components/useRangeState';
import CallTracePanel from './components/CallTracePanel';
import InteractionPanel from './components/InteractionPanel';
import FiltersBar from './components/FiltersBar';
import HelpButton from '../../help/HelpButton';

function DirectionCell({ value, row, onOpenTrace }) {
  const colorMap = {
    caller:     { bg: '#5AB6FF', label: 'Caller' },
    agent:      { bg: '#F5A623', label: 'Agent' },
    no_connect: { bg: '#9CA3AF', label: 'No connect' },
    unknown:    { bg: '#9CA3AF', label: '?' },
  };
  const c = colorMap[value] || colorMap.unknown;
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onOpenTrace(row.linkedid || row.uniqueid); }}
      title="Click for CEL trace (authoritative direction + ladder)"
      style={{
        padding: '2px 8px', borderRadius: 999,
        fontSize: 11, fontWeight: 600,
        background: c.bg, color: '#fff',
        border: 0, cursor: 'pointer',
      }}
    >
      {c.label}
      <span style={{ fontSize: 9, opacity: 0.85, marginLeft: 4 }}>(inferred)</span>
    </button>
  );
}

const DISPOS = ['ANSWERED', 'NO ANSWER', 'BUSY', 'FAILED', 'CONGESTION'];

export default function CDRDrilldown() {
  const [range, setRange, baseParams] = useRangeState('today');
  const [filters, setFilters] = useState({
    extension: '', queue_id: '', did: '', disposition: '',
    cluster: '', interaction_id: '',
  });
  const [pagination, setPagination] = useState({ limit: 100, offset: 0 });
  const [tracedCall, setTracedCall] = useState(null);
  const [openInteraction, setOpenInteraction] = useState(null);

  // CDR endpoint expects `extension` (not `agent`) as the query key.
  const apiParams = {
    ...baseParams,
    ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v)),
    ...pagination,
  };

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ['reports', 'cdr', apiParams],
    queryFn: () => reportsApi.cdr(apiParams),
    keepPreviousData: true,
  });

  const total = data?.total ?? 0;
  const pageStart = pagination.offset + 1;
  const pageEnd   = Math.min(total, pagination.offset + (data?.rows?.length || 0));

  return (
    <div>
      <RangeBar value={range} onChange={setRange} />
      <FiltersBar
        value={{
          agent:       filters.extension,
          queue_id:    filters.queue_id,
          did:         filters.did,
          disposition: filters.disposition,
          cluster:     filters.cluster,
        }}
        onChange={(v) => {
          setFilters({
            extension: v.agent || '',
            queue_id:  v.queue_id || '',
            did:       v.did || '',
            disposition: v.disposition || '',
            cluster:   v.cluster || '',
          });
          setPagination({ ...pagination, offset: 0 });
        }}
        show={['agent','queue','did','disposition']}
      />

      {/* CX_INTERACTION_ID Phase D: look up a whole call by its Interaction ID */}
      <div className="flex flex-wrap items-end gap-2 mb-2">
        <label className="text-xs text-surface-muted flex flex-col gap-1">
          Interaction ID
          <input
            type="text"
            value={filters.interaction_id}
            placeholder="e.g. 01M1HSWJEDZBGJDB5MXF474AHX"
            onChange={(e) => {
              setFilters({ ...filters, interaction_id: e.target.value.trim() });
              setPagination({ ...pagination, offset: 0 });
            }}
            className="px-2 py-1 text-xs rounded-thin border border-surface-border dark:border-surface-dark-border bg-transparent min-w-[280px] font-mono"
          />
        </label>
        {filters.interaction_id && (
          <button
            type="button"
            onClick={() => { setFilters({ ...filters, interaction_id: '' }); }}
            className="px-2 py-1 text-xs rounded-thin border border-surface-border dark:border-surface-dark-border hover:bg-surface-subtle dark:hover:bg-surface-dark-subtle"
          >
            ✕ Clear
          </button>
        )}
      </div>

      <div className="flex flex-wrap items-end gap-2 mb-3">
        <div className="ml-auto flex items-center gap-2">
          <HelpButton moduleKey="reports" />
          <ExportButtons report="cdr" params={apiParams} disabled={isLoading} />
          <button
            type="button"
            onClick={() => refetch()}
            className="px-2 py-1 text-xs rounded-thin border border-surface-border dark:border-surface-dark-border hover:bg-surface-subtle dark:hover:bg-surface-dark-subtle"
          >
            ↻ {isFetching ? '…' : 'Refresh'}
          </button>
        </div>
      </div>

      {isError && (
        <div className="text-sm text-red-600 dark:text-red-300 p-3 rounded-thin border border-red-300/40 mb-3">
          {error?.detail || error?.message || 'Failed to load report.'}
        </div>
      )}

      <div className="text-xs text-surface-muted mb-2">
        {total > 0 ? `Showing ${pageStart}–${pageEnd} of ${total.toLocaleString()} matched calls` : `${total} matched calls`}
      </div>

      <ReportTable
        headers={['calldate','src','dst','did','disposition','disconnect_direction','duration','billsec','uniqueid','cx_interaction_id']}
        rows={data?.rows || []}
        columnLabels={{
          calldate: 'Time',
          src: 'From',
          dst: 'To',
          did: 'DID',
          disposition: 'Result',
          disconnect_direction: 'Disconnect',
          duration: 'Dur',
          billsec: 'Billsec',
          uniqueid: 'Call ID',
          cx_interaction_id: 'Interaction ID',
        }}
        formatters={{
          disconnect_direction: (v, row) => (
            <DirectionCell
              value={v}
              row={row}
              onOpenTrace={(lid) => setTracedCall(lid)}
            />
          ),
          cx_interaction_id: (v) => (
            v ? (
              <button
                type="button"
                title="Open the full interaction (segments, legs, recordings)"
                onClick={() => setOpenInteraction(v)}
                className="font-mono text-[11px] text-brand-600 dark:text-brand-300 hover:underline"
              >
                {v}
              </button>
            ) : <span className="text-surface-muted">—</span>
          ),
        }}
      />

      {openInteraction && (
        <InteractionPanel
          interactionId={openInteraction}
          onClose={() => setOpenInteraction(null)}
        />
      )}

      {tracedCall && (
        <CallTracePanel linkedid={tracedCall} onClose={() => setTracedCall(null)} />
      )}

      {/* Paginator */}
      {total > pagination.limit && (
        <div className="flex items-center gap-2 mt-3">
          <button
            type="button"
            disabled={pagination.offset === 0}
            onClick={() => setPagination(p => ({ ...p, offset: Math.max(0, p.offset - p.limit) }))}
            className="px-2 py-1 text-xs rounded-thin border border-surface-border dark:border-surface-dark-border disabled:opacity-40"
          >
            ← Prev
          </button>
          <button
            type="button"
            disabled={pagination.offset + pagination.limit >= total}
            onClick={() => setPagination(p => ({ ...p, offset: p.offset + p.limit }))}
            className="px-2 py-1 text-xs rounded-thin border border-surface-border dark:border-surface-dark-border disabled:opacity-40"
          >
            Next →
          </button>
          <select
            value={pagination.limit}
            onChange={(e) => setPagination({ limit: Number(e.target.value), offset: 0 })}
            className="px-1 py-1 text-xs rounded-thin border border-surface-border dark:border-surface-dark-border bg-surface-base dark:bg-surface-dark-base"
          >
            {[50, 100, 200, 500].map(n => <option key={n} value={n}>{n} per page</option>)}
          </select>
        </div>
      )}
    </div>
  );
}
